import {
  combineLatest,
  distinctUntilChanged,
  map,
  Observable,
  of,
  switchMap,
} from 'rxjs';
import {
  action$,
  extractPayload,
  ofType,
  reduceState,
  streamReducer,
  withNamespace,
} from '@ardoq/rxbeach';
import {
  type I18nLocale,
  DEFAULT_ARDOQ_I18NLOCALE,
  type WithI18nLocale,
} from '@ardoq/i18n';
import { i18nLocale$ } from 'streams/i18nLocale$';
import {
  updateSettingsBarData,
  type UpdateSettingsBarDataArgs,
} from '@ardoq/view-settings';
import { ViewIds } from '@ardoq/api-types';
import { SettingsBarState } from './types';
import { getSharedExportFunctions } from '../../tabview/getSharedExportFunctions';
import {
  getRightSettingsMenuConfig,
  hasExportHandler,
  ViewIdsWithExportHandlers,
} from './getRightMenuSettingsConfig';
import {
  leftWorkspaceModeViewPaneTab$,
  rightWorkspaceModeViewPaneTab$,
  WorkspaceModeViewPaneTab,
} from '../workspaceModeViewPaneTabs$';
import { toWorkspaceModeMainViewInstanceId } from '../mainViewInstanceIds';
import { viewHasLegend } from '../../views/metaInfoTabs';
import { onViewSettingsUpdate } from '../../tabview/onViewSettingsUpdate';
import { logWarn } from '@ardoq/logging';
import { ViewSettingsBase } from '@ardoq/settings-bar';
import { isEqual } from 'lodash';
import { SettingsConfig } from '@ardoq/view-settings';
import {
  isWorkspaceModeBubbleChartSettingsControlsDependencies,
  toWorkspaceModeViewModifiers,
} from 'tabview/bubbleChart/workspaceMode/settingsControls';

const DEFAULT_SETTINGS_BAR_STATE: SettingsBarState = {
  viewId: ViewIds.NONE,
  leftMenu: [],
  rightMenu: [],
  i18nLocale: DEFAULT_ARDOQ_I18NLOCALE,
};

type ViewSettingsBarUpdate = UpdateSettingsBarDataArgs<ViewSettingsBase> &
  WithI18nLocale;

const toLeftMenu = (
  viewId: ViewIds,
  settingsControlsDependencies: ViewSettingsBarUpdate['settingsControlsDependencies'],
  i18nLocale: I18nLocale
): SettingsConfig[] => {
  if (viewId !== ViewIds.BUBBLE) {
    return settingsControlsDependencies.viewModifiers ?? [];
  }

  if (
    !isWorkspaceModeBubbleChartSettingsControlsDependencies(
      settingsControlsDependencies
    )
  ) {
    logWarn(
      new Error('Bubble chart published unrecognised settings dependencies')
    );
    return [];
  }

  return toWorkspaceModeViewModifiers({
    dependencies: settingsControlsDependencies,
    i18nLocale,
  });
};

const viewSettingsBarDataUpdated = (
  _: SettingsBarState,
  { settingsControlsDependencies, viewId, i18nLocale }: ViewSettingsBarUpdate
): SettingsBarState => {
  const {
    viewSettings,
    viewContext: { getContainer, zoomControls, knowledgeBaseLink },
    isEmptyView,
    viewInstanceId,
  } = settingsControlsDependencies;

  const leftMenu = toLeftMenu(viewId, settingsControlsDependencies, i18nLocale);

  const { getStyleSheets, addToPresentation, trackingFunction } =
    getSharedExportFunctions();

  if (!(zoomControls && knowledgeBaseLink)) {
    logWarn(
      new Error(
        'zoomControls and knowledgeBaseLink are required in settings bar'
      )
    );
    return { leftMenu, rightMenu: [], viewId, i18nLocale };
  }

  const rightMenu = hasExportHandler(viewId)
    ? getRightSettingsMenuConfig({
        viewInstanceId,
        viewSettings,
        viewId,
        viewHasLegend,
        onViewSettingsUpdate,
        getContainer,
        trackingFunction,
        getStyleSheets,
        isEmptyView,
        addToPresentation,
        zoomControls,
        knowledgeBaseLink,
        i18nLocale,
      })
    : [];

  return {
    rightMenu,
    leftMenu,
    viewId,
    i18nLocale,
  };
};

/** @param namespace a unique namespace. */
const getUpdateSettingsBarData$ = (
  namespace: string
): Observable<ViewSettingsBarUpdate> =>
  combineLatest([
    action$.pipe(
      withNamespace(namespace),
      ofType(updateSettingsBarData),
      extractPayload(),
      distinctUntilChanged((a, b) => isEqual(a, b))
    ),
    i18nLocale$,
  ]).pipe(map(([payload, i18nLocale]) => ({ ...payload, i18nLocale })));

const getSettingsBarInstanceStateForViewId = (
  viewId: ViewIds,
  uniqueNamespace: string
): Observable<SettingsBarState> => {
  const initialSettingsBarState: SettingsBarState = {
    ...DEFAULT_SETTINGS_BAR_STATE,
    viewId,
  };

  return action$.pipe(
    reduceState<SettingsBarState>(
      'viewPaneSettingsBar',
      initialSettingsBarState,
      [
        streamReducer(
          getUpdateSettingsBarData$(uniqueNamespace),
          viewSettingsBarDataUpdated
        ),
      ]
    )
  );
};

const VIEWS_WITH_EXTERNAL_SETINGS_BAR = new Set<ViewIdsWithExportHandlers>([
  ViewIds.BUBBLE,
  ViewIds.TIMELINE,
]);

export const hasViewPaneSettingsBar = (viewId: ViewIds): boolean =>
  hasExportHandler(viewId) && VIEWS_WITH_EXTERNAL_SETINGS_BAR.has(viewId);

const toPaneSettingsBarState$ = ({
  location,
  activeTabId,
  metaInfo,
}: WorkspaceModeViewPaneTab): Observable<SettingsBarState> => {
  if (activeTabId === ViewIds.NONE || metaInfo === null) {
    return of(DEFAULT_SETTINGS_BAR_STATE);
  }
  return getSettingsBarInstanceStateForViewId(
    activeTabId,
    toWorkspaceModeMainViewInstanceId(location, activeTabId)
  );
};

export const leftPaneSettingsBarState$ = leftWorkspaceModeViewPaneTab$.pipe(
  switchMap(toPaneSettingsBarState$)
);

export const rightPaneSettingsBarState$ = rightWorkspaceModeViewPaneTab$.pipe(
  switchMap(toPaneSettingsBarState$)
);
